import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiCheck } from 'react-icons/fi';
import API from '../../api/axios';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/helpers';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', isActive: true });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const fetchCategories = () => {
    setLoading(true);
    API.get('/categories/all')
      .then(({ data }) => setCategories(data))
      .catch(() => API.get('/categories').then(({ data }) => setCategories(data)).catch(() => {}))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCategories(); }, []);

  const openEdit = (cat) => {
    setEditId(cat._id);
    setForm({ name: cat.name, description: cat.description || '', isActive: cat.isActive });
    setImageUrl(cat.image || '');
    setShowForm(true);
  };

  const openNew = () => {
    setEditId(null);
    setForm({ name: '', description: '', isActive: true });
    setImageUrl('');
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditId(null); };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImageUrl(data.url);
      toast.success('Image uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error('Category name is required');
    setSaving(true);
    try {
      const payload = { ...form, image: imageUrl };
      if (editId) await API.put(`/categories/${editId}`, payload);
      else await API.post('/categories', payload);
      toast.success(editId ? 'Category updated!' : 'Category created!');
      fetchCategories();
      closeForm();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    try {
      await API.delete(`/categories/${id}`);
      toast.success('Category deleted');
      fetchCategories();
    } catch {
      toast.error('Error deleting category');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Categories</h1>
        <button onClick={openNew} className="btn-primary flex items-center gap-2">
          <FiPlus size={16} /> Add Category
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Category list */}
        <div className="lg:col-span-2 card overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}><td colSpan={3} className="px-4 py-3"><div className="h-8 bg-gray-100 rounded animate-pulse" /></td></tr>
                ))
              ) : categories.map((cat) => (
                <tr key={cat._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {cat.image ? (
                        <img src={getImageUrl(cat.image)} alt={cat.name} className="w-8 h-8 rounded-lg object-contain bg-gray-100" />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-600 font-bold text-sm">{cat.name[0]}</span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-sm text-gray-800">{cat.name}</p>
                        <p className="text-xs text-gray-400 font-mono">{cat.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${cat.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {cat.isActive ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(cat)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg">
                        <FiEdit2 size={15} />
                      </button>
                      <button onClick={() => handleDelete(cat._id, cat.name)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg">
                        <FiTrash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {categories.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-400">No categories yet</div>
          )}
        </div>

        {/* Form */}
        {showForm && (
          <div className="card p-6">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-bold text-gray-800">{editId ? 'Edit Category' : 'New Category'}</h2>
              <button onClick={closeForm} className="p-1 hover:bg-gray-100 rounded-lg"><FiX size={16} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Name *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="input-field" placeholder="Category name" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="input-field" placeholder="Optional description" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Category Image</label>
                <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} className="text-sm text-gray-500 file:mr-3 file:btn-primary file:text-xs" />
                {imageUrl && <img src={getImageUrl(imageUrl)} alt="" className="w-16 h-16 object-contain rounded-lg bg-gray-50 mt-2" />}
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="w-4 h-4 accent-primary-500" />
                <span className="text-sm text-gray-700">Active</span>
              </label>
              <div className="flex gap-2 pt-2">
                <button type="submit" disabled={saving} className="btn-primary flex items-center gap-1 flex-1">
                  <FiCheck size={14} /> {saving ? 'Saving...' : 'Save'}
                </button>
                <button type="button" onClick={closeForm} className="btn-secondary px-4">Cancel</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCategories;
